import React from 'react';
import { Meridiem, TimeParts, TimePickerProps } from '../../../types/DesignSystem/TimePicker';
import FieldTitle from '../FieldTitle/FieldTitle';
import Typography from '../Typography/Typography';
import Popover from '../Popover/Popover';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import { Clock } from '../Iconography/Icons';
import { CONTROL_HEIGHT, FIELD_INSET } from '../controlSize';
import { FIELD_BOX, fieldBorder, FIELD_REST, FIELD_DISABLED } from '../fieldChrome';
import { FOCUS_RING } from '../focusRing';
import {
  EMPTY_PARTS,
  HOUR_OPTIONS,
  MERIDIEM_OPTIONS,
  displayFromParts,
  minuteOptions,
  partsFromTyped,
  partsFromValue,
  valueFromParts,
} from './timeParts';
import { useControlSize } from '../DensityProvider/DensityProvider';

/**
 * The design system's time field — a typeable `hh:mm AM/PM` input with a picker
 * popover, storing an unambiguous 24-hour `HH:mm` string.
 *
 * ---
 *
 * **What § 290 changed, and what it deliberately did not.**
 *
 * § 129 built this from two `Dropdown`s and declared 24-hour "the contract, not a
 * preference". The stored contract is untouched — `value` and `onChange` still speak
 * `HH:mm`, `''` still means nothing chosen, and the connector's payload
 * (`startDate + "T" + startTime + ":00Z"`) is byte-identical. What changed is the
 * WIDGET, because "the date field is a calendar and the time field is two selects"
 * was an inconsistency the user met on the same row of the same form.
 *
 * § 129's real finding survives intact and is worth restating, because it is the
 * thing that looks like it is being reversed here and is not: the fault with
 * `<input type="time">` was never that it showed AM/PM. It was that the BROWSER chose
 * the format, per locale, with no relation to what the field stored or how the label
 * read — and that no stylesheet or spec of ours could reach the popup. A DS control
 * that shows `05:06 PM`, says `stored as 17:06` underneath, and renders every option
 * as real DOM has none of those problems. The format is ours, it is the same in every
 * browser, and Playwright can drive it.
 *
 * **Three things carried over from § 129 unchanged:**
 *
 * - **A partial selection reports `''`.** An hour with no minute is not a time, and
 *   `ScheduleForm.validateSchedule` rejects an empty `startTime` — that has to stay
 *   true. § 290 adds a third part to be partial about: picking 5 and 06 without
 *   saying AM or PM is a real ambiguity on a field labelled UTC, so it stays `''`
 *   until the meridiem is chosen. The half already picked stays on screen, rendered
 *   as `05:-- --`, for the same reason the two selects kept showing `--`.
 * - **The sync effect only fires when `value` actually changes**, which is what lets
 *   that partial survive the `''` it reports. It also compares against the parts it
 *   already holds, so the value we just emitted coming back in does NOT interrupt
 *   someone mid-type.
 * - **`minuteStep` defaults to 1.** It governs the COLUMN only; typing accepts any
 *   minute, so a coarse column can never lock a user out of 17:07.
 *
 * **Why the popover opens from the clock button and not from the field.** `Popover`
 * moves focus into its surface on open (that is what makes Escape work). A field that
 * opened on click or focus would therefore snatch the caret away the instant you tried
 * to type in it. The two affordances are separate on purpose: the field is for typing,
 * the button is for picking. `DatePicker` opens on click because its field is not
 * meaningfully typeable.
 */
const TimePicker = React.forwardRef<HTMLDivElement, TimePickerProps>(function TimePicker({
  label,
  value: valueProp,
  defaultValue,
  onChange,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  minuteStep = 1,
  defaultMinute,
  size: sizeProp,
  labelSize,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  const uid = React.useId();
  const inputId = `${uid}-input`;
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  const formatId = `${uid}-format`;
  /* § 357 — see TextField. The legacy default, `value = ''`, is the fallback. */
  const [value, setValue] = useControllableState(valueProp, defaultValue, '');
  const storedId = `${uid}-stored`;

  const [parts, setParts] = React.useState<TimeParts>(() => partsFromValue(value));
  /** The raw text while the field has the caret; `null` means "render from parts". */
  const [typing, setTyping] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  /*
    § 290 — the field's own validation, and the only kind it can honestly do: the text
    in it is not a time at all. Everything else is the CALLER's judgement — a partial
    selection, or a time in the past — which is why `error` stays a prop and this only
    ever ORs into it.

    It is raised on BLUR, never while typing: `5` on the way to `5:06 pm` is not a
    mistake, and a field that goes red under the caret teaches people to ignore it.
  */
  const [unreadable, setUnreadable] = React.useState(false);

  const partsRef = React.useRef(parts);
  partsRef.current = parts;

  /*
    Re-sync only on a genuinely different value — a form reset, or a record arriving
    after first paint. Two guards, each earning its place:

    - the dependency is `value`, so the `''` we report for a partial (which does not
      change a `value` that is already `''`) cannot wipe the half on screen;
    - the comparison against `partsRef` catches the OTHER direction — a complete value
      we just emitted echoing back would otherwise clear `typing` and snap the field to
      its canonical form while the user was still typing it.
  */
  React.useEffect(() => {
    const next = partsFromValue(value);
    const current = partsRef.current;
    if (current.h12 === next.h12 && current.mm === next.mm && current.mer === next.mer) return;
    setParts(next);
    setTyping(null);
    setUnreadable(false);
  }, [value]);

  const apply = (next: TimeParts) => {
    setParts(next);
    const nextValue = valueFromParts(next);
    setValue(nextValue);
    onChange?.(nextValue);
  };

  const handleType = (text: string) => {
    setTyping(text);
    setUnreadable(false);
    apply(partsFromTyped(text));
  };

  /*
    On blur the field normally snaps to its canonical rendering. It must NOT do that
    when the text cannot be read — `displayFromParts` of nothing is `''`, so snapping
    would silently swallow what the user typed and leave an empty box with no hint
    that anything went wrong. The text stays, and says why.
  */
  const handleBlur = () => {
    if (typing === null) return;
    if (typing.trim() === '') {
      setTyping(null);
      setUnreadable(false);
      return;
    }
    const parsed = partsFromTyped(typing);
    if (!parsed.h12 && !parsed.mm && !parsed.mer) {
      setUnreadable(true);
      return;
    }
    setTyping(null);
    setUnreadable(false);
    /* `defaultMinute` — a bare typed hour completes on blur, never mid-type. */
    if (defaultMinute && parsed.h12 && !parsed.mm) apply({ ...parsed, mm: defaultMinute });
  };

  /* A pick always wins over a half-typed string — the user has just said what they
     meant with a click, so the field snaps to the canonical rendering of it. */
  /*
    A pick always wins over a half-typed string — the user has just said what they
    meant with a click, so the field snaps to the canonical rendering of it.

    **AM is the default half of the day.** Picking an hour and a minute is enough to
    make a time; the meridiem column shows AM selected from the moment the picker
    opens, so the default is one the user can SEE and change rather than a guess made
    behind their back. That visibility is the whole difference: § 290's first cut
    required all three and reported `''` for 5:06 with no meridiem, which read as the
    picker ignoring two clicks.
  */
  const pick = (patch: Partial<TimeParts>) => {
    setTyping(null);
    setUnreadable(false);
    const next = { ...partsRef.current, ...patch };
    /* `defaultMinute` fills an EMPTY minute when the hour is picked; a chosen one stays. */
    const mm = patch.h12 && !next.mm && defaultMinute ? defaultMinute : next.mm;
    apply({ ...next, mm, mer: next.mer || 'AM' });
  };

  const minutes = React.useMemo(() => minuteOptions(minuteStep), [minuteStep]);
  const stored = valueFromParts(parts);
  const displayed = typing ?? displayFromParts(parts);

  /* § 356 — the caller's error, its error text, or the field's own format judgement. */
  const invalid = fieldInvalid(error, errorMessage) || unreadable;
  const describedBy = [
    unreadable ? formatId : undefined,
    ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
    stored ? storedId : undefined,
  ].filter(Boolean).join(' ') || undefined;

  /*
    The picker's three columns. Buttons rather than a `listbox`, deliberately: the
    field beside them is typeable, so this surface is a convenience, and "Hour, 5,
    button" is a truthful announcement that costs no aria-activedescendant machinery.
    Roving tabindex keeps 74 options to ONE tab stop per column.
  */
  const columnsRef = React.useRef<Array<HTMLDivElement | null>>([]);

  const moveWithin = (column: HTMLDivElement | null, from: EventTarget, delta: number) => {
    if (!column) return;
    const options = Array.from(column.querySelectorAll<HTMLButtonElement>('button'));
    const index = options.indexOf(from as HTMLButtonElement);
    const next = options[Math.min(options.length - 1, Math.max(0, index + delta))];
    next?.focus();
  };

  const moveAcross = (fromColumn: number, delta: number) => {
    const column = columnsRef.current[fromColumn + delta];
    if (!column) return;
    const target =
      column.querySelector<HTMLButtonElement>('button[tabindex="0"]') ??
      column.querySelector<HTMLButtonElement>('button');
    target?.focus();
  };

  const onColumnKeyDown = (columnIndex: number) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    const column = columnsRef.current[columnIndex];
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); moveWithin(column, event.target, 1); break;
      case 'ArrowUp': event.preventDefault(); moveWithin(column, event.target, -1); break;
      case 'ArrowRight': event.preventDefault(); moveAcross(columnIndex, 1); break;
      case 'ArrowLeft': event.preventDefault(); moveAcross(columnIndex, -1); break;
      case 'Home': event.preventDefault(); moveWithin(column, event.target, -999); break;
      case 'End': event.preventDefault(); moveWithin(column, event.target, 999); break;
      default: break;
    }
  };

  /*
    A field that goes disabled closes its picker — and, more to the point, FORGETS it
    was open, so re-enabling cannot pop a picker nobody asked for back onto the screen.

    This effect is the WHOLE mechanism. The first cut also gated the surface with
    `open={open && !disabled}`, and no mutation could make that half fail: the effect
    runs before anything can observe the difference, so the guard was belt over
    braces. § 129 deleted a `lastEmitted` ref from this same component for the same
    reason — a guard no test can falsify is a guard nobody can maintain — and the
    second occurrence is why the rule is worth restating rather than quietly obeying.
  */
  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  /* Bring the current selection into view when the picker opens — a 60-row minute
     column that lands at 00 hides the answer the user is looking at. */
  React.useEffect(() => {
    if (!open) return;
    for (const column of columnsRef.current) {
      column?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')
        ?.scrollIntoView?.({ block: 'center' });
    }
  }, [open]);

  const renderColumn = (
    columnIndex: number,
    columnLabel: string,
    options: readonly string[],
    selected: string,
    onPick: (option: string) => void,
    testidPart: string,
  ) => (
    <div
      role="group"
      aria-label={columnLabel}
      ref={(node) => { columnsRef.current[columnIndex] = node; }}
      onKeyDown={onColumnKeyDown(columnIndex)}
      className="tw-flex tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-max-h-[196px] tw-min-w-[64px] tw-pe-1"
    >
      {options.map((option, index) => {
        const isSelected = option === selected;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={isSelected}
            /* One tab stop per column: the selection, or the first option when there
               is none yet. */
            tabIndex={isSelected || (!selected && index === 0) ? 0 : -1}
            onClick={() => onPick(option)}
            data-testid={dataTestId ? `${dataTestId}-${testidPart}-${option}` : undefined}
            className={`tw-rounded-ctl tw-px-3 tw-py-1 tw-text-body-primary tw-text-center tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING} ${
              isSelected
                ? 'tw-bg-accent-tint tw-text-accent tw-font-semibold'
                : 'tw-text-ink hover:tw-bg-surface-hover'
            }`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );

  const fieldClassName = `tw-w-full ${CONTROL_HEIGHT[size]} ${FIELD_INSET[size]} tw-pe-9 ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled })} tw-text-body-primary tw-text-ink placeholder:tw-text-ink-secondary ${disabled ? FIELD_DISABLED : FIELD_REST}`;

  return (
    <div className={className}>
      {label && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the field's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          htmlFor={inputId}
          labelId={labelId}
        />
      )}

      {/* `className` lands on the wrapper; `ref` and every other native attribute reach
          the GROUP — the element that is the control. Same split as § 129 shipped. */}
      <div
        {...rest}
        ref={ref}
        role="group"
        aria-labelledby={label ? labelId : undefined}
        data-testid={dataTestId}
        className="tw-relative"
      >
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={displayed}
          placeholder="hh:mm AM"
          disabled={disabled}
          onChange={(event) => handleType(event.target.value)}
          onBlur={handleBlur}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          aria-describedby={describedBy}
          aria-label={label ? undefined : 'Time'}
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
          className={fieldClassName}
        />

        {/*
          The affordance, and a real button — unlike `DatePicker`'s glyph, which is
          inert decoration because clicking that field opens its calendar. Here the
          field belongs to the keyboard, so the picker needs its own target.
        */}
        {/*
          Centred with `inset-y-0` + flex, NOT `top-1/2 -translate-y-1/2`.

          **A transform creates a stacking context**, and this span is the popover's
          positioned ancestor — so `tw-z-overlay` on the surface was being confined to
          a context whose own z-index is `auto`. Every later sibling in the form then
          painted OVER the picker: on the connector's Schedule Import the Repeat
          dropdown showed through it. The z-index was never the problem; the ancestor
          was. (`DatePicker` does not hit this: its calendar is portalled with
          `strategy: 'fixed'`.)
        */}
        <span className="tw-absolute tw-inset-y-0 tw-end-1.5 tw-flex tw-items-center">
          <Popover
            open={open}
            onOpenChange={setOpen}
            aria-label="Choose a time"
            /*
              `bottom-end`, not the default `bottom-start`. The Popover anchors to its
              TRIGGER, and the trigger here is a 24px button at the right edge of the
              field — so start-alignment hangs 240px of picker off that edge and out of
              whatever card the field sits in. End-alignment pins the surface's right
              edge to the button's, which puts it under the field it belongs to.
              (`DatePicker`'s calendar starts at the left edge because its anchor is
              the whole field, not an icon.)
            */
            placement="bottom-end"
            data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
            className="tw-p-3"
            trigger={
              <button
                type="button"
                disabled={disabled}
                aria-label="Choose a time"
                data-testid={dataTestId ? `${dataTestId}-open` : undefined}
                className={`tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-ctl tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING} ${
                  disabled
                    ? 'tw-text-ink-disabled tw-cursor-not-allowed'
                    : 'tw-text-ink-tertiary hover:tw-text-ink hover:tw-bg-surface-hover'
                }`}
              >
                <Clock size={16} aria-hidden="true" />
              </button>
            }
          >
            <div className="tw-flex tw-gap-2">
              {renderColumn(0, 'Hour', HOUR_OPTIONS, parts.h12, (h12) => pick({ h12 }), 'hour')}
              {renderColumn(1, 'Minute', minutes, parts.mm, (mm) => pick({ mm }), 'minute')}
              {renderColumn(
                2,
                'AM or PM',
                MERIDIEM_OPTIONS,
                /* Shown as selected before anything is picked — see `pick`. The PARTS
                   stay empty, so an untouched field still displays its placeholder
                   rather than `--:-- AM`. */
                parts.mer || 'AM',
                (mer) => pick({ mer: mer as Meridiem }),
                'meridiem',
              )}
            </div>
          </Popover>
        </span>
      </div>

      {unreadable && (
        <Typography
          role="alert"
          id={formatId}
          variant="caption-metadata"
          data-testid={dataTestId ? `${dataTestId}-format-error` : undefined}
          className="tw-mt-1 tw-text-tone-danger-ink"
        >
          Enter a time like 5:06 PM or 17:06
        </Typography>
      )}

      {/* § 302 — the one helper seam (`fieldHelper.tsx`). The format alert above stays its
          own element: it is the field's judgement, this is the caller's. */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />

      {/*
        The honesty line. The field reads in 12-hour because that is how people read
        times; the payload is 24-hour. Showing both is what stops § 129's original
        complaint — a widget and its data disagreeing about what time means — from
        coming back in a new shape. It appears only once the value EXISTS, so it can
        never contradict a half-finished field.
      */}
      {stored && (
        <Typography
          id={storedId}
          variant="caption-metadata"
          data-testid={dataTestId ? `${dataTestId}-stored` : undefined}
          className="tw-mt-1 tw-text-ink-tertiary"
        >
          {`stored as ${stored}`}
        </Typography>
      )}
    </div>
  );
});

export default TimePicker;
